import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Plus, Settings, MessageSquare, FileText, Save, ArrowLeft, AlertCircle, Trash2 } from 'lucide-react';
import NodeSidebar from './NodeSidebar';
import EdgeSidebar from './EdgeSidebar';
import { templateService } from '../../services/templateService';
import { botService } from '../../services/botService';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// Check if ReactFlow is available
let ReactFlow, MiniMap, Controls, Background, useNodesState, useEdgesState, addEdge, Connection, Edge, Node, NodeTypes, Position, Handle, getSmoothStepPath, ReactFlowProvider, useReactFlow;

try {
  const reactFlowModule = require('reactflow');
  ReactFlow = reactFlowModule.default;
  MiniMap = reactFlowModule.MiniMap;
  Controls = reactFlowModule.Controls;
  Background = reactFlowModule.Background;
  useNodesState = reactFlowModule.useNodesState;
  useEdgesState = reactFlowModule.useEdgesState;
  addEdge = reactFlowModule.addEdge;
  Connection = reactFlowModule.Connection;
  Edge = reactFlowModule.Edge;
  Node = reactFlowModule.Node;
  NodeTypes = reactFlowModule.NodeTypes;
  Position = reactFlowModule.Position;
  Handle = reactFlowModule.Handle;
  getSmoothStepPath = reactFlowModule.getSmoothStepPath;
  ReactFlowProvider = reactFlowModule.ReactFlowProvider;
  useReactFlow = reactFlowModule.useReactFlow;
  
  // Import CSS
  require('reactflow/dist/style.css');
} catch (error) {
  console.warn('ReactFlow not installed. Please run: npm install reactflow');
}

// Custom Node Component
// --- BotNode (patched) ---
const BotNode = ({ data, selected }) => {
  return (
    <div className={`px-4 py-2 shadow-md rounded-md bg-white border-2 min-w-[200px] ${
      selected ? 'border-blue-500' : 'border-gray-200'
    }`}>
      {/* Single input (top) */}
      <Handle
        id="input-top"
        type="target"
        position={Position.Top}
        className="w-3 h-3 bg-gray-400"
      />

      <div className="flex items-center space-x-2">
        {data.type === 'template' ? (
          <MessageSquare className="w-4 h-4 text-blue-500" />
        ) : (
          <FileText className="w-4 h-4 text-green-500" />
        )}
        <div>
          <div className="font-bold text-sm">{data.label}</div>
          <div className="text-xs text-gray-500">
            {data.type === 'template' ? 'Template' : 'Free Text'}
          </div>
        </div>
      </div>

      {/* Three outputs: right, bottom, left (Option C) */}
      <Handle
        id="output-right"
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-gray-400"
      />
      <Handle
        id="output-bottom"
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 bg-gray-400"
      />
      <Handle
        id="output-left"
        type="source"
        position={Position.Left}
        className="w-3 h-3 bg-gray-400"
      />
    </div>
  );
};
// --- end BotNode ---


// Custom Edge Component with label
// --- BotEdge (patched) ---
const BotEdge = ({
  id,
  data,
  selected,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {}
}) => {
  // compute smooth path if helper exists, otherwise fallback
  const [edgePath, labelX, labelY] = getSmoothStepPath
    ? getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
      })
    : ['', 0, 0];

  const edgeId = id || data?.id || 'edge-default';

  return (
    <>
      <path
        id={`edge-${edgeId}`}
        className="react-flow__edge-path"
        d={edgePath}
        style={{
          ...style,
          strokeWidth: selected ? 3 : 2,
          stroke: selected ? '#3b82f6' : '#b1b1b7',
          cursor: 'pointer',
          // allow clicking on the stroke only (improves click reliability)
          pointerEvents: 'stroke'
        }}
      />

      {data?.label && (
        <text
          x={labelX}
          y={labelY}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: 12,
            fill: selected ? '#3b82f6' : '#666',
            pointerEvents: 'none',
            fontWeight: selected ? 'bold' : 'normal'
          }}
        >
          {data.label}
        </text>
      )}
    </>
  );
};
// --- end BotEdge ---


const nodeTypes = {
  botNode: BotNode,
};

const edgeTypes = {
  botEdge: BotEdge,
};

const getSaveErrorMessage = (error) => {
  const backendMessage = error?.response?.data?.message;
  const rawMessage = backendMessage || error?.message || '';
  const lower = rawMessage.toLowerCase();

  if (lower.includes('published version already exists')) {
    return 'A published version already exists for this bot. Unpublish first or continue editing draft.';
  }
  if (lower.includes('active session')) {
    return 'This bot has active sessions. Please end active sessions before changing status and try again.';
  }
  if (lower.includes('another bot is already published')) {
    return 'Another bot is already published. Unpublish it first, then retry.';
  }
  if (backendMessage) {
    return backendMessage;
  }
  return 'Failed to save bot configuration. Please try again.';
};

// Fallback component when ReactFlow is not available
const BotBuilderFallback = ({ bot, onBack }) => {
  return (
    <div className="flex h-screen bg-gray-100">
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">ReactFlow Not Installed</h2>
          <p className="text-gray-600 mb-6">
            To use the Bot Builder, you need to install ReactFlow. Please run the following command:
          </p>
          <div className="bg-gray-800 text-green-400 p-4 rounded-lg font-mono text-sm mb-6">
            npm install reactflow
          </div>
          <p className="text-gray-600 mb-6">
            After installation, refresh the page to use the Bot Builder.
          </p>
          {onBack && (
            <button
              onClick={onBack}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Bot Management</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ReactFlow-enabled BotBuilder component
const BotBuilderWithReactFlow = ({ bot, onBack }) => {
  const { isAdmin } = useAuth();
  const { botId } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showEdgeSidebar, setShowEdgeSidebar] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentBot, setCurrentBot] = useState(bot);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [originalNodes, setOriginalNodes] = useState([]);
  const [originalEdges, setOriginalEdges] = useState([]);
  const nodeId = useRef(0);
  const reactFlowInstance = useReactFlow();

  // Load bot data when component mounts or botId changes
  useEffect(() => {
    if (botId && !currentBot) {
      loadBotDetails(); // Load full bot details first
      loadBotData();
    } else if (bot) {
      setCurrentBot(bot);
      loadBotData();
    }
  }, [botId, bot]);

  // Load approved templates and bot data on component mount
  React.useEffect(() => {
    loadTemplates();
    if (bot) {
      loadBotData();
    }
  }, [bot]);

  // Check for changes whenever nodes or edges change
  React.useEffect(() => {
    console.log('=== CHANGE DETECTION DEBUG ===');
    console.log('Current nodes:', nodes);
    console.log('Original nodes:', originalNodes);
    console.log('Current edges:', edges);
    console.log('Original edges:', originalEdges);
    
    // Compare only essential properties to avoid ReactFlow internal property changes
    const nodesChanged = nodes.length !== originalNodes.length ||
      nodes.some((node, index) => {
        const original = originalNodes[index];
        return !original ||
          node.id !== original.id ||
          node.type !== original.type ||
          JSON.stringify(node.position) !== JSON.stringify(original.position) ||
          JSON.stringify(node.data) !== JSON.stringify(original.data);
      });
      
    const edgesChanged = edges.length !== originalEdges.length ||
      edges.some((edge, index) => {
        const original = originalEdges[index];
        return !original ||
          edge.id !== original.id ||
          edge.source !== original.source ||
          edge.target !== original.target ||
          JSON.stringify(edge.data) !== JSON.stringify(original.data);
      });
    
    const hasChanges = nodesChanged || edgesChanged;
    
    console.log('Nodes changed:', nodesChanged);
    console.log('Edges changed:', edgesChanged);
    console.log('Has unsaved changes:', hasChanges);
    console.log('==============================');
    
    setHasUnsavedChanges(hasChanges);
  }, [nodes, edges, originalNodes, originalEdges]);

  // Fit view after nodes are loaded
  React.useEffect(() => {
    if (nodes.length > 0 && reactFlowInstance) {
      // Small delay to ensure nodes are rendered
      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.1 });
      }, 100);
    }
  }, [nodes, reactFlowInstance]);

  // Debug: Log current nodes state
  React.useEffect(() => {
    console.log('Current nodes state:', nodes);
    console.log('Number of nodes in state:', nodes.length);
  }, [nodes]);

  // Debug: Monitor hasUnsavedChanges state
  React.useEffect(() => {
    console.log('hasUnsavedChanges state changed:', hasUnsavedChanges);
  }, [hasUnsavedChanges]);

  const loadBotData = async () => {
    const targetBot = currentBot || (botId ? { botId: botId } : bot);
    if (!targetBot) return;
    
    try {
      const response = isAdmin 
        ? await botService.getAdminBotConfiguration(targetBot.botId)
        : await botService.getBotConfiguration(targetBot.botId);
      console.log('Loaded bot data:', response); // Debug log
      console.log('Number of nodes from backend:', response.nodes?.length); // Add this
      console.log('Raw nodes data:', response.nodes); // Add this
      
      if (response.nodes && response.edges) {
        // No transformation needed - botService already returns ReactFlow format
        console.log('Setting nodes directly:', response.nodes);
        console.log('Node IDs:', response.nodes.map(n => n.id));
        console.log('Node positions:', response.nodes.map(n => n.position));
        
        // Check for duplicate IDs
        const nodeIds = response.nodes.map(n => n.id);
        const uniqueIds = [...new Set(nodeIds)];
        if (nodeIds.length !== uniqueIds.length) {
          console.warn('Duplicate node IDs detected:', nodeIds);
        }
        
        // Remove any markerEnd properties from loaded edges (edges are undirected, no arrows)
        const edgesWithMarkers = response.edges.map(edge => {
          const { markerEnd, ...edgeWithoutMarker } = edge;
          return edgeWithoutMarker;
        });
        
        console.log('=== LOADING BOT DATA ===');
        console.log('Setting nodes:', response.nodes);
        console.log('Setting edges:', edgesWithMarkers);
        
        setNodes(response.nodes);
        setEdges(edgesWithMarkers);
        
        // Store original state for change detection
        const originalNodesCopy = JSON.parse(JSON.stringify(response.nodes));
        const originalEdgesCopy = JSON.parse(JSON.stringify(edgesWithMarkers));
        
        console.log('Setting original nodes:', originalNodesCopy);
        console.log('Setting original edges:', originalEdgesCopy);
        
        setOriginalNodes(originalNodesCopy);
        setOriginalEdges(originalEdgesCopy);
        setHasUnsavedChanges(false);
        
        console.log('hasUnsavedChanges set to false');
        console.log('=============================');
        
        // Update nodeId ref to avoid conflicts
        let maxId = 0;
        response.nodes.forEach(node => {
          const match = node.id.match(/node-(\d+)/);
          if (match) {
            maxId = Math.max(maxId, parseInt(match[1]));
          }
        });
        nodeId.current = maxId + 1;
      }
    } catch (error) {
      console.error('Error loading bot data:', error);
    }
  };

  const loadBotDetails = async () => {
    if (!botId) return;
    
    try {
      const botDetails = isAdmin 
        ? await botService.getAdminBot(botId)
        : await botService.getBot(botId);
      
      setCurrentBot(botDetails);
      console.log('Loaded bot details:', botDetails);
    } catch (error) {
      console.error('Error loading bot details:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateService.getApprovedTemplates();
      setTemplates(response || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const onConnect = useCallback(
    (params) => {
      const edgeId = `edge-${Date.now()}`;
      const newEdge = {
        ...params,
        id: edgeId,
        type: 'botEdge',
        data: {
          label: 'Click to edit',
          id: edgeId
        }
      };
      console.log('Creating new edge:', newEdge);
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const onEdgeClick = useCallback((event, edge) => {
    console.log('Edge clicked:', edge);
    setSelectedEdge(edge);
    setShowEdgeSidebar(true);
    setSelectedNode(null);
    setShowSidebar(false);
  }, []);

  // --- addNode (patched) ---
  const addNode = useCallback(() => {
    const isFirstNode = nodes.length === 0;

    // Default center: screen center in window coordinates
    const screenCenter = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

    // Convert screen coords -> flow coords accounting for pan/zoom.
    // We try multiple common APIs to be robust across ReactFlow versions.
    let flowCenter = { x: screenCenter.x, y: screenCenter.y };

    try {
      if (reactFlowInstance) {
        // Preferred: project() (available in many ReactFlow builds)
        if (typeof reactFlowInstance.project === 'function') {
          flowCenter = reactFlowInstance.project(screenCenter);
        } else {
          // Fallback: read viewport transform (x, y, zoom) then convert
          const viewport = (typeof reactFlowInstance.getViewport === 'function')
            ? reactFlowInstance.getViewport()
            : (reactFlowInstance.viewport || { x: 0, y: 0, zoom: 1 });

          const { x: vx = 0, y: vy = 0, zoom = 1 } = viewport;
          flowCenter = {
            x: (screenCenter.x - vx) / zoom,
            y: (screenCenter.y - vy) / zoom
          };
        }
      }
    } catch (err) {
      // If anything fails, we keep the screen center fallback (reasonable)
      console.warn('Could not project screen->flow coords, using fallback center', err);
    }

    const newNodeId = isFirstNode ? 'start-node' : `node-${nodeId.current++}`;
    const newNode = {
      id: newNodeId,
      type: 'botNode',
      position: { x: Math.round(flowCenter.x), y: Math.round(flowCenter.y) },
      data: {
        label: isFirstNode ? 'Start' : `Node ${nodeId.current}`,
        type: 'template',
        content: '',
        templateId: null,
        isStart: isFirstNode,
        variables: {}
      },
    };

    setNodes((nds) => [...nds, newNode]);
  }, [setNodes, nodes.length, reactFlowInstance]);
  // --- end addNode ---


  const onNodeClick = useCallback((event, node) => {
    console.log('Node clicked:', node);
    setSelectedNode(node);
    setShowSidebar(true);
    setSelectedEdge(null);
    setShowEdgeSidebar(false);
  }, []);

  const onPaneClick = useCallback((event) => {
    console.log('Pane clicked, clearing selections');
    setSelectedNode(null);
    setSelectedEdge(null);
    setShowSidebar(false);
    setShowEdgeSidebar(false);
  }, []);

  const updateNode = useCallback((nodeId, updates) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          // If updates include variables, merge them properly
          const newData = { ...node.data, ...updates };
          if (updates.variables) {
            newData.variables = updates.variables;
          }
          return { ...node, data: newData };
        }
        return node;
      })
    );
    
    // Update selected node if it's the one being updated
    if (selectedNode && selectedNode.id === nodeId) {
      const newData = { ...selectedNode.data, ...updates };
      if (updates.variables) {
        newData.variables = updates.variables;
      }
      setSelectedNode(prev => ({
        ...prev,
        data: newData
      }));
    }
  }, [setNodes, selectedNode]);

  const updateEdge = useCallback((edgeId, updates) => {
    console.log('Updating edge:', edgeId, updates);
    
    setEdges((eds) => {
      const updatedEdges = eds.map((edge) => {
        if (edge.id !== edgeId) return edge;
        
        // Create a new edge object with updated data to ensure ReactFlow detects the change
        // Using spread operator creates a new object reference which triggers re-render
        const updatedEdge = {
          ...edge,
          data: { 
            ...edge.data, 
            ...updates 
          }
        };
        console.log('Updated edge object:', updatedEdge);
        return updatedEdge;
      });
      console.log('Updated edges array:', updatedEdges);
      return updatedEdges;
    });
    
    // Update selected edge if it's the one being updated
    if (selectedEdge && selectedEdge.id === edgeId) {
      const updatedEdge = {
        ...selectedEdge,
        data: { ...selectedEdge.data, ...updates }
      };
      console.log('Updated selected edge:', updatedEdge);
      setSelectedEdge(updatedEdge);
    }
  }, [setEdges, selectedEdge]);

  const deleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    
    if (selectedNode && selectedNode.id === nodeId) {
      setSelectedNode(null);
      setShowSidebar(false);
    }
  }, [setNodes, setEdges, selectedNode]);

  const deleteEdge = useCallback((edgeId) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
    
    if (selectedEdge && selectedEdge.id === edgeId) {
      setSelectedEdge(null);
      setShowEdgeSidebar(false);
    }
  }, [setEdges, selectedEdge]);

  // Keyboard delete handler for nodes and edges
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle Delete or Backspace keys
      if (event.key !== 'Delete' && event.key !== 'Backspace') {
        return;
      }

      // Don't trigger if user is typing in an input field, textarea, or contenteditable element
      const target = event.target;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Only allow deletion if bot is in draft status
      if (currentBot?.botStatus !== '10001') {
        return;
      }

      // Handle node deletion
      if (selectedNode) {
        // Prevent deletion of start node
        if (selectedNode.id === 'start-node' || selectedNode.data?.isStart) {
          return;
        }

        // Show confirmation dialog (matching button behavior)
        if (window.confirm('Are you sure you want to delete this node?')) {
          deleteNode(selectedNode.id);
        }
        return;
      }

      // Handle edge deletion (no confirmation needed, matching existing behavior)
      if (selectedEdge) {
        deleteEdge(selectedEdge.id);
      }
    };

    // Add event listener to window
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedNode, selectedEdge, currentBot, deleteNode, deleteEdge]);

  const closeSidebar = () => {
    setShowSidebar(false);
    setSelectedNode(null);
  };

  const closeEdgeSidebar = () => {
    console.log('Closing edge sidebar');
    setShowEdgeSidebar(false);
    setSelectedEdge(null);
  };

  const saveBot = async () => {
    // Check if we have a flow ID from any source
    const targetBotId = currentBot?.botId || botId || bot?.botId;
    if (!targetBotId) {
      console.error('Cannot save bot: No bot ID available');
      return;
    }
    
    try {
      setSaving(true);
      // Get current nodes and edges with their positions from ReactFlow
      const currentNodes = reactFlowInstance.getNodes();
      const currentEdges = reactFlowInstance.getEdges();
      
      // Log edges being saved
      console.log('=== SAVING BOT ===');
      console.log('Edges being saved:', currentEdges);
      console.log('===================');
      
      if (isAdmin) {
        await botService.saveAdminBotConfiguration(targetBotId, currentNodes, currentEdges);
      } else {
        await botService.saveBotConfiguration(targetBotId, currentNodes, currentEdges);
      }
      // Update original state after successful save
      setOriginalNodes(JSON.parse(JSON.stringify(currentNodes)));
      setOriginalEdges(JSON.parse(JSON.stringify(currentEdges)));
      setHasUnsavedChanges(false);
      console.log('Bot saved successfully');
      toast.success('Bot saved successfully');
    } catch (error) {
      console.error('Error saving bot:', error);
      const errorMessage = getSaveErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      if (onBack) {
        onBack();
      } else {
        // Navigate back to bots page
        navigate('/bots');
      }
    }
  };

  const handleSaveAndExit = async () => {
    await saveBot();
    setShowUnsavedDialog(false);
    if (onBack) {
      onBack();
    } else {
      navigate('/bots');
    }
  };

  const handleExitWithoutSaving = () => {
    setShowUnsavedDialog(false);
    if (onBack) {
      onBack();
    } else {
      navigate('/bots');
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Main Flow Area */}
      <div className="flex-1 relative">
        {/* Header */}
        <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            {onBack && (
              <button
                onClick={handleBackClick}
                className="bg-gray-500 hover:bg-gray-600 text-white p-2 rounded-lg shadow-lg flex items-center space-x-2 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back</span>
              </button>
            )}
            <button
              onClick={addNode}
              disabled={currentBot?.botStatus !== '10001'}
              className={`p-2 rounded-lg shadow-lg flex items-center space-x-2 transition-colors ${
                currentBot?.botStatus !== '10001' 
                  ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
              title={currentBot?.botStatus !== '10001' ? 'Cannot add nodes to non-draft bot' : 'Add Node'}
            >
              <Plus className="w-5 h-5" />
              <span>Add Node</span>
            </button>
            <button
              onClick={() => {
                if (selectedNode && window.confirm('Are you sure you want to delete this node?')) {
                  deleteNode(selectedNode.id);
                }
              }}
              disabled={!selectedNode || selectedNode.id === 'start-node' || selectedNode.data?.isStart || currentBot?.botStatus !== '10001'}
              className={`p-2 rounded-lg shadow-lg flex items-center space-x-2 transition-colors ${
                currentBot?.botStatus !== '10001' 
                  ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
                  : 'bg-red-500 hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white'
              }`}
              title={currentBot?.botStatus !== '10001' ? 'Cannot delete nodes from non-draft bot' : 'Delete Node'}
            >
              <Trash2 className="w-5 h-5" />
              <span>Delete Node</span>
            </button>
          </div>
          
          {currentBot && (
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-semibold text-gray-800">{currentBot.botName}</h2>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                currentBot.botStatus === '10001' ? 'bg-yellow-100 text-yellow-800' : 
                currentBot.botStatus === '10002' ? 'bg-green-100 text-green-800' : 
                currentBot.botStatus === '10003' ? 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {currentBot.botStatus === '10001' ? 'DRAFT' : 
                 currentBot.botStatus === '10002' ? 'PUBLISHED' : 
                 currentBot.botStatus === '10003' ? 'ARCHIVED' : currentBot.botStatus}
              </span>
              {currentBot.botStatus !== '10001' && (
                <span className="text-sm text-gray-500 italic">
                  {currentBot.botStatus === '10002' ? '(Read-only: Published bot)' : 
                   currentBot.botStatus === '10003' ? '(Read-only: Archived bot)' : '(Read-only)'}
                </span>
              )}
              <button
                onClick={() => {
                  console.log('=== SAVE BUTTON CLICKED ===');
                  console.log('saving:', saving);
                  console.log('hasUnsavedChanges:', hasUnsavedChanges);
                  console.log('botStatus:', currentBot?.botStatus);
                  console.log('Button disabled:', saving || !hasUnsavedChanges || currentBot?.botStatus !== '10001');
                  console.log('===========================');
                  saveBot();
                }}
                disabled={saving || !hasUnsavedChanges || currentBot?.botStatus !== '10001'}
                className={`p-2 rounded-lg shadow-lg flex items-center space-x-2 transition-colors ${
                  currentBot?.botStatus !== '10001' 
                    ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
                    : 'bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white'
                }`}
                title={currentBot?.botStatus !== '10001' ? 
                       currentBot?.botStatus === '10002' ? 'Cannot save published bot' : 
                       currentBot?.botStatus === '10003' ? 'Cannot save archived bot' : 'Cannot save bot' : 
                       'Save changes'}
              >
                <Save className="w-5 h-5" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.1 }}
          minZoom={0.5}                      
          maxZoom={1}
          className="bg-gray-50"
          defaultEdgeOptions={{
            type: 'botEdge'
          }}
        >
          <Controls />
          <MiniMap />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
      </div>

      {/* Node Sidebar */}
      {showSidebar && selectedNode && (
        <NodeSidebar
          node={selectedNode}
          templates={templates}
          onUpdate={updateNode}
          onDelete={deleteNode}
          onClose={closeSidebar}
          loading={loading}
          botStatus={currentBot?.botStatus}
        />
      )}

      {/* Edge Sidebar */}
      {showEdgeSidebar && selectedEdge && (
        <EdgeSidebar
          edge={selectedEdge}
          onUpdate={updateEdge}
          onDelete={deleteEdge}
          onClose={closeEdgeSidebar}
          loading={loading}
          botStatus={currentBot?.botStatus}
        />
      )}

      {/* Unsaved Changes Dialog */}
      {showUnsavedDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Unsaved Changes
            </h3>
            <p className="text-gray-600 mb-6">
              You have unsaved changes to this bot. Do you want to save before leaving?
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={() => setShowUnsavedDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExitWithoutSaving}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Exit Without Saving
              </button>
              <button
                onClick={handleSaveAndExit}
                disabled={saving}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                {saving ? 'Saving...' : 'Save & Exit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main BotBuilder component that handles conditional rendering
const BotBuilder = ({ bot, onBack }) => {
  // Check if ReactFlow is available
  if (!ReactFlow) {
    return <BotBuilderFallback bot={bot} onBack={onBack} />;
  }

  return (
    <ReactFlowProvider>
      <BotBuilderWithReactFlow bot={bot} onBack={onBack} />
    </ReactFlowProvider>
  );
};

export default BotBuilder;